import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import type { ParentAccessPolicy } from '@/types';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import styles from './AdminParentAccessPage.module.css';

type BooleanPolicyKey = Exclude<keyof ParentAccessPolicy, 'guardianInvitationMethod'>;

const ACCESS_SETTINGS: BooleanPolicyKey[] = [
  'legalGuardianAccess',
  'financialSponsorAccess',
  'academicAccess',
  'pickupAuthorization',
  'communicationAccess',
  'minorStudentRestrictions',
];

const INVITATION_METHODS: ParentAccessPolicy['guardianInvitationMethod'][] = ['EMAIL', 'SMS', 'BOTH'];

const METHOD_LABELS: Record<ParentAccessPolicy['guardianInvitationMethod'], string> = {
  EMAIL: 'admin.parentAccess.methodEmail',
  SMS: 'admin.parentAccess.methodSms',
  BOTH: 'admin.parentAccess.methodBoth',
};

export function AdminParentAccessPage() {
  const { t } = useTranslation();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  const { state, reload, replace } = useAsyncData<ParentAccessPolicy>(
    `admin-parent-access-${institutionId}`,
    () => api.admin.parentAccess.get(institutionId),
    !!institutionId,
  );

  const save = useCallback(
    async (patch: Partial<ParentAccessPolicy>) => {
      if (state.status !== 'ready') return;
      const previous = state.data;
      // Optimistic: the toggle flips immediately, and flips back if the save fails.
      replace({ ...previous, ...patch });
      setSaving(true);
      try {
        await api.admin.parentAccess.update(institutionId, patch);
      } catch {
        replace(previous);
        toast.error(t('common.saveFailed'));
      } finally {
        setSaving(false);
      }
    },
    [institutionId, replace, state, t, toast],
  );

  if (state.status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('admin.parentAccess.loading')}</div>
      </div>
    );
  }

  if (state.status === 'error') {
    const error = state.error;
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name={error.isMissingEndpoint ? 'info' : 'error'} size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.parentAccess.title')}</h2>
          <p className={styles.emptyStateBody}>
            {t(error.isMissingEndpoint ? 'platform.failure.missing.body' : 'platform.failure.generic.body')}
          </p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      </div>
    );
  }

  const policy = state.data;

  return (
    <div className={styles.page}>
      <div className="page-header">
        <h1>{t('admin.parentAccess.title')}</h1>
        <p className="page-subtitle">{t('admin.parentAccess.subtitle')}</p>
      </div>

      <div className={styles.contentGrid}>
        <Card className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('admin.parentAccess.invitationMethods')}</h2>
          </div>
          <div className={styles.list}>
            {INVITATION_METHODS.map((method) => (
              <div key={method} className={styles.toggleRow}>
                <span className={styles.toggleLabel}>{t(METHOD_LABELS[method])}</span>
                <button
                  type="button"
                  role="radio"
                  aria-checked={policy.guardianInvitationMethod === method}
                  disabled={saving}
                  className={`${styles.toggle} ${policy.guardianInvitationMethod === method ? styles.toggleActive : ''}`}
                  onClick={() => save({ guardianInvitationMethod: method })}
                >
                  <span className={styles.toggleKnob} />
                </button>
              </div>
            ))}
          </div>

          <div className={`${styles.sectionHeader} ${styles.sectionHeaderSpaced}`}>
            <h2 className={styles.sectionTitle}>{t('admin.parentAccess.relationshipVerification')}</h2>
          </div>
          <div className={styles.toggleRow}>
            <span className={styles.toggleLabel}>{t('admin.parentAccess.relationshipVerification')}</span>
            <button
              type="button"
              role="switch"
              aria-checked={policy.requireRelationshipVerification}
              disabled={saving}
              className={`${styles.toggle} ${policy.requireRelationshipVerification ? styles.toggleActive : ''}`}
              onClick={() => save({ requireRelationshipVerification: !policy.requireRelationshipVerification })}
            >
              <span className={styles.toggleKnob} />
            </button>
          </div>
        </Card>

        <Card className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('admin.parentAccess.settings')}</h2>
          </div>
          <div className={styles.list}>
            {ACCESS_SETTINGS.map((key) => (
              <div key={key} className={styles.toggleRow}>
                <span className={styles.toggleLabel}>{t(`admin.parentAccess.${key}`)}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={policy[key]}
                  disabled={saving}
                  className={`${styles.toggle} ${policy[key] ? styles.toggleActive : ''}`}
                  onClick={() => save({ [key]: !policy[key] })}
                >
                  <span className={styles.toggleKnob} />
                </button>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
